import { printSlice } from './features/print/printSlice';
import { configureStore, ThunkAction, Action, isPlain } from '@reduxjs/toolkit';
import { NQError, NQValueKind } from '@visonum/network-quality-sdk';
import measurementReducer from './features/measurement/measurementSlice';
import hints, { initHints } from "./features/hints/hintsSlice"
import errorReducer from "./features/errors/errorSlice"
import * as history from "./features/history/historySlice"
import { isRunningInBrowser } from './helper/ssr';
import customerCommunicationSlice from './features/customerCommunication/customerCommunicationSlice';
import { NODE_ENV } from './config';
import { logger } from './helper/logger';
import * as R from 'ramda';
import debugLogSlice, { initDebugLog } from './features/debugLog/debugLogSlice';
import privacyPolicySlice from './features/privacyPolicy/privacyPolicySlice';
import { NQInternalError } from '@visonum/network-quality-sdk/dist/src/main/NQError';
import sentryReportsSlice, { updateBeforeUnloadCaused } from './features/sentryReports/sentryReportsSlice';

const isNqValue = (value: any): boolean => {
  return !R.isNil(value?.kind) && value.kind in NQValueKind;
}

/**
 * Marks NQValue and NQError instances as serializable for Redux store
 */
const isSerializable = (value: any): boolean => {
  if (isNqValue(value)) {
    return true;
  }
  if (value instanceof NQError) {
    return true;
  }
  if (value instanceof NQInternalError) {
    return true;
  }
  if (value instanceof Date) {
    return true;
  }
  if (value instanceof Error) {
    return true;
  }
  if (isRunningInBrowser()) {
    if (value instanceof XMLHttpRequest) {
      return true;
    }
  }
  return isPlain(value);
};

/**
 * Filters out the member methods from NQValue and NQError objects during serialization
 */
const getEntries = (value: any): [string, any][] => {
  const result = Object.entries(value)
  if (isNqValue(value) || value instanceof NQError) {
    return result.filter(([k, v]) => typeof v !== 'function')
  }
  return result
}

export const store = configureStore({
  reducer: {
    measurement: measurementReducer,
    history: history.reducer,
    hints: hints.reducer,
    error: errorReducer,
    customerCommunication: customerCommunicationSlice.reducer,
    print: printSlice.reducer,
    debugLog: debugLogSlice.reducer,
    privacyPolicy: privacyPolicySlice.reducer,
    sentryReports: sentryReportsSlice.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({      
      serializableCheck: {
        isSerializable,
        getEntries
      },      
    }),    
});

const setupStore = () => {
  store.dispatch(history.initHistory());
  if (isRunningInBrowser()) {
    store.dispatch(initDebugLog());
    store.dispatch(initHints());

    if (NODE_ENV === "test") {
      logger.info(`Set store to window object. Store is ${store}`);
      window.store = store;
      window.setBeforeUnload = () => {
        store.dispatch(updateBeforeUnloadCaused(true));
      }
    } else {
      logger.info("NOT set store to window object.")
    }  
  } 
}

export type AppStore = typeof store;
export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;

setupStore();